import { Box } from '@mantine/core';
import StyledSelect from '@/components/StyledInputFields/StyledSelect.tsx';
import { ConfigFieldValue } from '@/types/automationFields.ts';
import CustomLoadingOverlay from '@/components/CustomLoadingOverlay.tsx';
import useRetrieveSelectFieldOptionsIntegrations from '@/hooks/api/integrations/useRetrieveSelectFieldOptions.ts';
import { useMemo } from 'react';
import useFieldConfigError from '@/hooks/useFieldConfigError.ts';
import { IntegrationSelectConfigField } from '@/types/integrations.ts';

type props = {
  stepId: string;
  field: IntegrationSelectConfigField;
  fieldValue: any;
  fieldValueConfig: ConfigFieldValue;
  onChange: (updated_value: any) => void;
  disabled: boolean;
};

const isDynamicSelectField = (field: IntegrationSelectConfigField) => !field.static_options;

const SelectField = ({
  stepId,
  field,
  fieldValue,
  fieldValueConfig,
  onChange,
  disabled,
}: props) => {
  const loadDynamicOptions =
    isDynamicSelectField(field) && !fieldValueConfig.is_disabled && !disabled;

  const { data: dynamicOptions, isLoading: isLoadingDynamicOptions } =
    useRetrieveSelectFieldOptionsIntegrations(stepId, field.id, loadDynamicOptions);

  const isLoading = isDynamicSelectField(field) ? isLoadingDynamicOptions : false;

  const options = useMemo(() => {
    return isDynamicSelectField(field) ? dynamicOptions : field.options;
  }, [dynamicOptions, field]);

  const error = useFieldConfigError(stepId, fieldValueConfig);

  return (
    <Box pos="relative">
      <CustomLoadingOverlay visible={isLoading} />

      <StyledSelect
        label={field.display_name}
        description={field.description ?? undefined}
        placeholder={field.placeholder ?? undefined}
        data={options}
        value={fieldValue}
        onChange={(value, _) => onChange(value)}
        required={field.required}
        allowDeselect={!field.required}
        clearable={!field.required}
        error={error?.message}
        disabled={fieldValueConfig.is_disabled}
      />
    </Box>
  );
};

export default SelectField;
